import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, FileText, Lock, Download } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AIAdvisor({ domains }) {
  const navigate = useNavigate();
  const { isPaid } = useAuth();
  const [advice, setAdvice] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    api.get("/ai/quota").then((q) => setQuota(q.data)).catch(() => {});
  }, []);

  const canRun = isPaid || (quota ? quota.remaining > 0 : false);

  const run = async (runMode = "advisor") => {
    setLoading(true);
    setMode(runMode);
    setAdvice("");
    try {
      const resp = await fetch(`${API}/ai/advisor`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domains, mode: runMode }),
      });
      if (resp.status === 402) {
        const j = await resp.json().catch(() => ({}));
        toast.error(j.detail || "Upgrade to continue using AI briefs.");
        setQuota((q) => (q ? { ...q, remaining: 0 } : q));
        setLoading(false);
        return;
      }
      if (resp.status === 429) {
        const j = await resp.json().catch(() => ({}));
        toast.error(j.detail || "AI advisor hourly limit reached.");
        setLoading(false);
        return;
      }
      if (!resp.ok || !resp.body) {
        toast.error("AI Advisor failed. Try scanning the domain first.");
        setLoading(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) { acc += obj.delta; setAdvice(acc); }
            if (obj.error) toast.error("AI error: " + obj.error);
          } catch {}
        }
      }
      try { const q = await api.get("/ai/quota"); setQuota(q.data); } catch {}
    } catch (e) {
      toast.error("AI Advisor failed. Try scanning the domain first.");
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    if (!advice) return;
    try {
      const res = await api.post(
        "/brief/pdf",
        { markdown: advice, title: (domains && domains[0]) || "Link Website Pro", mode: mode || "build_brief" },
        { responseType: "blob" }
      );
      const ct = res.headers?.["content-type"] || "";
      const isPdf = ct.includes("pdf");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const base = ((domains && domains[0]) || "brief").replace(/[^a-z0-9.-]+/gi, "-");
      a.download = `${base}-${mode === "build_brief" ? "build-brief" : "advisor"}.${isPdf ? "pdf" : "html"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      toast.success("Brief exported as PDF.");
    } catch (e) {
      if (e?.response?.status === 402) { toast.error("Exports require a Pro plan."); navigate("/pricing"); }
      else toast.error("Export failed");
    }
  };

  const renderMd = (text) =>
    text.split("\n").map((line, i) => {
      if (/^#{1,6}\s/.test(line))
        return <h4 key={i} className="font-heading text-[#4da3ff] font-semibold text-sm mt-4 mb-1">{line.replace(/^#{1,6}\s/, "")}</h4>;
      if (/^\s*[-*]\s/.test(line))
        return <li key={i} className="ml-4 list-disc text-zinc-300 text-sm leading-relaxed">{line.replace(/^\s*[-*]\s/, "")}</li>;
      if (/^\d+\.\s/.test(line))
        return <div key={i} className="text-zinc-200 text-sm font-medium mt-3">{line}</div>;
      if (!line.trim()) return <div key={i} className="h-2" />;
      return <p key={i} className="text-zinc-400 text-sm leading-relaxed">{line}</p>;
    });

  return (
    <div
      data-testid="ai-advisor-panel"
      className={`relative bg-[#121212] border border-[#007AFF]/30 rounded-xl p-6 overflow-hidden ${loading ? "ai-glow" : ""}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#007AFF]/10 to-transparent pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#4da3ff]" />
            <h3 className="font-heading font-semibold text-white">AI Website Advisor</h3>
            {quota && !(quota.free_trial && quota.limit === 0) && (
              <span className="text-[11px] font-mono text-zinc-500" data-testid="ai-quota">
                {quota.free_trial ? `${quota.remaining} free ${quota.remaining === 1 ? "analysis" : "analyses"} left` : `${quota.remaining}/${quota.limit} left this hour`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!canRun ? (
              <button
                data-testid="advisor-upgrade-btn"
                onClick={() => navigate("/pricing")}
                className="btn-brand text-white rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Lock size={14} /> {quota?.free_trial && quota?.limit > 0 ? "Free trial used — Upgrade for unlimited" : "Upgrade to unlock AI briefs"}
              </button>
            ) : (
              <>
                <button
                  data-testid="run-ai-advisor-btn"
                  onClick={() => run("advisor")}
                  disabled={loading || !domains?.length}
                  className="bg-transparent border border-[#007AFF]/50 hover:bg-[#007AFF]/15 disabled:opacity-40 text-[#4da3ff] rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
                >
                  {loading && mode === "advisor" ? <><Loader2 size={15} className="animate-spin" /> Analyzing…</> : <><Sparkles size={15} /> Get Advice</>}
                </button>
                <button
                  data-testid="run-build-brief-btn"
                  onClick={() => run("build_brief")}
                  disabled={loading || !domains?.length}
                  className="btn-brand disabled:opacity-40 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2"
                >
                  {loading && mode === "build_brief" ? <><Loader2 size={15} className="animate-spin" /> Building brief…</> : <><FileText size={15} /> Emergent Build Brief</>}
                </button>
              </>
            )}
          </div>
        </div>

        {!advice && !loading && (
          <p className="text-zinc-500 text-sm mt-4 font-mono">
            {domains?.length > 1
              ? `Compare ${domains.length} domains, or generate an Emergent-ready build brief with evidence, fail points & a phased plan.`
              : "Get Advice = quick recommendations. Emergent Build Brief = a buildable spec with evidence, current fail points, legacy risks & a phased plan."}
          </p>
        )}

        {advice && (
          <div className="mt-4 font-mono max-h-[600px] overflow-y-auto pr-2" data-testid="ai-advice-output">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              {mode === "build_brief" ? (
                <div className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] text-[#4da3ff] bg-[#007AFF]/10 border border-[#007AFF]/30 rounded px-2 py-1">
                  Emergent Build Brief
                </div>
              ) : <div />}
              {!loading && (
                <button
                  data-testid="export-brief-pdf-btn"
                  onClick={exportPdf}
                  className="bg-transparent border border-white/20 hover:bg-white/10 text-white rounded-md px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5"
                >
                  <Download size={13} /> Export as PDF
                </button>
              )}
            </div>
            {renderMd(advice)}
          </div>
        )}
      </div>
    </div>
  );
}
